import React, { FC, useEffect } from 'react'

import { styled } from '@mui/system'
import { ObjectTypeEnum } from '../../../types/SubCondition'
import { FIELD_STATEMENT_OPERATORS } from '../../../context/CustomField/CustomFieldTypes'
import { ValueError } from '../../../store/groupConditionsSlice'
import { InputAdornment, TextField } from '@mui/material'
import { useLocalizedText } from '../../../context/Localization/localizationHooks'
import { LocalizedText } from '../../../context/Localization/LocalizationTypes'
import { useViewModeReadOnly } from '../../../context/ViewMode/viewModeHooks'
import NumberField from '../Currency/NumberField'

const RootDollarStatement = styled('div')(() => ({
    height: '40px',
    display: 'flex',
    alignItems: 'center',
}))
const SpanOverride = styled('span')(() => ({
    color: '#263238', 
    fontWeight: 600, 
    fontSize: 16, 
    fontFamily: 'Open Sans',
    margin: '0px 8px',
}))

const DollarSignAdornment = <InputAdornment position="start">$</InputAdornment>
export interface DollarStatementProps {
    operator: typeof FIELD_STATEMENT_OPERATORS['Amount'][number]['key']
    objectType: ObjectTypeEnum
    values: Array<string>
    valueError: ValueError
    onChange: (newValues: Array<string>) => void
}
const DollarStatement: FC<DollarStatementProps> = (props) => {
    const AndLabel = useLocalizedText(LocalizedText.AndLabel)

    const numberFormatter = new Intl.NumberFormat('en-us', { style: 'currency', currency: 'USD' })

    const isReadOnly = useViewModeReadOnly()

    useEffect(() => {
        if (props.operator === 'EQUALS' || props.operator === 'GREATER_THAN' || props.operator === 'LESS_THAN' || props.operator === 'NOT_EQUALS') {
            props.onChange([props.values[0] || ''])
        } else if (props.operator === 'BETWEEN' || props.operator === 'NOT_BETWEEN') {
            // if second > first return second, if second <= first return first + 1
            const firstValue = Math.max(Number(props.values[0]), 0)
            const secondValue = Number(props.values[1]) > firstValue ? props.values[1] : String(firstValue + 1)
            props.onChange([String(firstValue) || '', secondValue || ''])
        }
    }, [props.operator])

    const dollarOneError = props.valueError && props.valueError.errors.findIndex(error => error.valueIndex === 0 && error.message) !== -1
    const dollarTwoError = props.valueError && props.valueError.errors.findIndex(error => error.valueIndex === 1 && error.message) !== -1

    return (
        <RootDollarStatement>
            {
                (props.operator === 'EQUALS' || props.operator === 'GREATER_THAN' || props.operator === 'LESS_THAN' || props.operator === 'NOT_EQUALS') && (
                    <NumberField 
                        value={props.values[0] || ''}
                        disabled={isReadOnly}
                        error={dollarOneError}
                        startAdornment={DollarSignAdornment}
                        onChange={(value) => {
                            const newValue = Math.max(Number(value), 0)
                            props.onChange([String(newValue)])
                        }}
                    />
                )
            }
            {
                (props.operator === 'BETWEEN' || props.operator === 'NOT_BETWEEN') && (
                    <>
                        <NumberField 
                            value={props.values[0] || ''}
                            disabled={isReadOnly}
                            error={dollarOneError}
                            startAdornment={DollarSignAdornment}
                            onChange={(value) => {
                                const firstValue = Math.max(Number(value), 0)
                                const secondValue = Number(props.values[1]) > firstValue ? props.values[1] : String(firstValue + 1)
                                props.onChange([String(firstValue) || '', secondValue || ''])
                            }}
                        />
                        <SpanOverride>{AndLabel}</SpanOverride>
                        <NumberField 
                            value={props.values[1] || ''}
                            disabled={!props.values[0] || isReadOnly}
                            error={dollarTwoError}
                            startAdornment={DollarSignAdornment}
                            min={Number(props.values[0] || '') + 1}
                            onChange={(value) => {
                                const firstValue = Math.max(Number(props.values[0] || ''), 0)
                                const newValue = Number(value || '')
                                const secondValue = newValue > firstValue ? newValue : firstValue + 1
                                props.onChange([ props.values[0] || '', String(secondValue) || ''])
                            }}
                        />
                    </>
                )
            }
        </RootDollarStatement>
    )
}

export default DollarStatement